import { useAuth } from '@/store/useAuth';

import { api, API_BASE } from './api';

export interface ResumoEsteira {
  total: number;
  por_estado: Record<string, number>;
  /** Onde a esteira parou: a primeira etapa falhada, ou a primeira pendente. */
  travada_em: string | null;
  faixa: { etapa: string; rotulo: string; estado: string; manual: boolean }[];
}

/** Uma lauda do telejornal, do jeito que a tela precisa dela. */
export interface Lauda {
  id: number;
  mos_id: string;
  ro_id: string;
  story_id: string;
  story_num: string | null;
  /** Rótulo do jornal: o espelho do NRCS, ou o canal quando o espelho não nomeia. */
  jornal: string;
  dia: string;
  /** `true` quando o dia veio da hora do ar; `false` quando veio da ingestão. */
  dia_e_do_ar: boolean;
  ar: string | null;
  ingerida_em: string | null;
  stage: string;
  /** Como o objeto se resolveu: sem_midia, a_espera_de_midia ou completo. */
  completude: string;
  /** Sobe a cada re-resolucao material do objeto. */
  versao: number;
  praca_id: string | null;
  praca_nome: string | null;
  /** Itens de vídeo, imagem ou áudio. Crédito não conta — não promete bytes. */
  itens_de_midia: number;
  pauta_id: number | null;
  titulo: string;
  estado: string;
  erro: string | null;
  chars: number;
  /** Resumo da esteira desta lauda — a faixa da lista sai daqui. */
  esteira?: ResumoEsteira;
  /** Categoria fora do gate (`anatomia`, `servico`, `sem_minimo`) — null quando passou. */
  oculta_por?: CategoriaFora | null;
  /** O veredito do gate: MATÉRIA, NOTA ou FORA, com o motivo. */
  gate?: {
    decisao: 'materia' | 'nota' | 'fora';
    categoria: string;
    motivo: string;
    caracteres: number | null;
  };
  /** O FATO: a história que junta esta lauda às outras peças dela (a chamada,
   *  a cabeça, o VT, o pé, e a mesma história no jornal seguinte). `null` =
   *  lauda sem fato, e ela aparece sozinha como sempre apareceu. */
  fato?: FatoDaLauda | null;
}

export interface FatoDaLauda {
  id: string;
  titulo: string;
  chave: string | null;
  /** Quantas peças o fato tem, contando esta. */
  pecas: number;
}

export interface PecaDoFato {
  lauda_id: number;
  titulo: string;
  jornal: string;
  praca_nome: string | null;
  ar: string | null;
  dia: string;
  estado: string;
  wp_id: number | null;
  site: string | null;
  chars: number;
  /** O repórter que o NRCS da praça declarou — quem fez a reportagem. */
  reporter?: string | null;
  /** O formato da peça no NRCS: VT, LINK, LOC OFF, NOTA, ESTUDIO... */
  formato?: string | null;
}

export interface FatoAberto {
  id: string;
  titulo: string;
  chave: string | null;
  criado_em: string | null;
  pecas: PecaDoFato[];
  total: number;
  /** Quem fez a reportagem, somado das peças — a história tem autoria. */
  creditos?: string[];
}

/** O que o gate deixa fora da tela por padrão (parâmetros em /engenharia). */
export type CategoriaFora = 'formato' | 'anatomia' | 'servico' | 'sem_minimo';

export interface Faceta {
  rotulo: string;
  total: number;
}

export interface FacetaDia {
  dia: string;
  total: number;
}

export interface PracaAba {
  id: string;
  nome: string;
  slug: string;
}

export interface LaudasResponse {
  laudas: Lauda[];
  total: number;
  /** Quantas o gate deixou fora, por categoria. */
  ocultas?: Partial<Record<CategoriaFora, number>>;
  mostrando_ocultas?: boolean;
  pracas: PracaAba[];
  jornais: Faceta[];
  dias: FacetaDia[];
  /** As 13 etapas: a faixa desenha completa mesmo onde ainda não há linha. */
  catalogo: { etapa: string; ordem: number; rotulo: string }[];
  hoje: string;
}

/** O que o contrato do fato teve a dizer sobre esta geração (ADR-0022). */
export interface ContratoAviso {
  modo?: string;
  modo_configurado?: string;
  passou?: boolean;
  motivo?: string;
  aviso?: string;
  tentativas?: number;
}

export interface GeracaoResponse {
  lauda_id: number;
  wp_id: number | null;
  wp_link: string | null;
  title: string | null;
  contrato: ContratoAviso;
}

/** Uma etapa da esteira, do jeito que a tela precisa dela. */
export interface Etapa {
  etapa: string;
  rotulo: string;
  ordem: number;
  /** pendente | rodando | pronto | falhou | pulada | manual */
  estado: string;
  versao: number;
  /** `true` quando um humano fixou a saida — ela nao e re-executada no refazer. */
  manual: boolean;
  ator: string | null;
  erro: string | null;
  custo_usd: number | null;
  saida: Record<string, any> | null;
  iniciado_em: string | null;
  terminado_em: string | null;
}

export interface EsteiraResponse {
  lauda_id: number;
  story_id: string;
  /** O post da matéria no WordPress — o card abre o editor e sobe mídia por ele. */
  wp_id?: number | null;
  wp_link?: string | null;
  site?: string | null;
  titulo: string | null;
  estado_pauta: string | null;
  completude: string;
  etapas: Etapa[];
  resumo: ResumoEsteira;
  /** As 13 etapas do contrato — a faixa desenha completa mesmo sem linha ainda. */
  catalogo: { etapa: string; ordem: number; rotulo: string }[];
}

export interface RefazerResponse {
  etapa: string;
  marcadas: string[];
  wp_id?: number | null;
  wp_link?: string | null;
  recusa?: string;
  erro?: string;
}

export interface FiltroLaudas {
  praca_id?: string;
  jornal?: string;
  dia?: string;
  mostrar_ocultas?: boolean;
  /** Categorias fora do gate que o operador marcou para ver também. */
  mostrar?: CategoriaFora[];
}

export interface MidiaEnviada {
  lauda_id: number;
  papel: 'foto' | 'video';
  media_id: number;
  url: string;
  wp_id: number;
}

export const jornaisApi = {
  listar: (f: FiltroLaudas = {}): Promise<LaudasResponse> => {
    const q = new URLSearchParams();
    if (f.praca_id) q.set('praca_id', f.praca_id);
    if (f.jornal) q.set('jornal', f.jornal);
    if (f.dia) q.set('dia', f.dia);
    if (f.mostrar_ocultas) q.set('mostrar_ocultas', 'true');
    if (f.mostrar && f.mostrar.length > 0) q.set('mostrar', f.mostrar.join(','));
    const qs = q.toString();
    return api.get<LaudasResponse>(`/jornais/laudas${qs ? `?${qs}` : ''}`);
  },
  gerar: (laudaId: number): Promise<GeracaoResponse> =>
    api.post<GeracaoResponse>(`/jornais/laudas/${laudaId}/gerar`, {}),

  /** As peças de um fato: a história inteira que a TV contou em pedaços. */
  fato: (fatoId: string): Promise<FatoAberto> =>
    api.get<FatoAberto>(`/jornais/fatos/${fatoId}`),

  /** A esteira daquela lauda: o que cada etapa devolveu e em que estado. */
  esteira: (laudaId: number): Promise<EsteiraResponse> =>
    api.get<EsteiraResponse>(`/jornais/laudas/${laudaId}/esteira`),

  /** Fixa a saida de uma etapa. Devolve o que ficou pendente por causa disso. */
  corrigir: (laudaId: number, etapa: string, saida: Record<string, unknown>) =>
    api.post<{ etapa: string; invalidadas: string[] }>(
      `/jornais/laudas/${laudaId}/etapas/${etapa}/corrigir`,
      { saida },
    ),

  /** Refaz dali para a frente. Etapa corrigida a mao e preservada. */
  refazer: (laudaId: number, etapa: string) =>
    api.post<RefazerResponse>(`/jornais/laudas/${laudaId}/etapas/${etapa}/refazer`, {}),

  /**
   * Foto vira a imagem destacada; vídeo entra no topo do corpo. O post continua
   * no status em que estava. fetch cru porque FormData não leva o Content-Type
   * JSON do wrapper — mas o token precisa ir.
   */
  subirMidia: async (laudaId: number, arquivo: File): Promise<MidiaEnviada> => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    const token = useAuth.getState().token;
    const r = await fetch(`${API_BASE}/jornais/laudas/${laudaId}/midia`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      credentials: 'include',
      body: form,
    });
    if (!r.ok) {
      let detalhe = '';
      try {
        detalhe = String(((await r.json()) as { detail?: unknown })?.detail ?? '');
      } catch {
        /* corpo sem JSON: fica o código */
      }
      throw new Error(detalhe || `falha ${r.status} ao subir a mídia`);
    }
    return (await r.json()) as MidiaEnviada;
  },
};
